import { Suspense } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { cn, preventTextInput } from "@/lib/utils"
import { textAreaDefaultMaxLength } from "@/models/common"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { MedicalHistory } from "@/modules/patients/detail/medical-history"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { NUTRITIONAL_CONDITIONS, OBESITIES, REASONS } from "@/modules/patients/detail/therapy-prescription/ch/constants"
import {
  NutritionalCounselling,
  NutritionalCounsellingSchema,
} from "@/modules/patients/detail/therapy-prescription/ch/schema/nutritional-counselling-schema"
import { chNutritionalCounsellingFragment$key } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/chNutritionalCounsellingFragment.graphql"
import { chNutritionalCounsellingFragment } from "@/modules/patients/detail/therapy-prescription/ch/services/chNutritionalCounsellingFragment"
import { useChCreateNutritionalCounsellingMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChCreateNutritionalCounsellingMutation"
import { useChDeleteNutritionalCounsellingMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChDeleteNutritionalCounsellingMutation"
import { useChGenerateNutritionalCounsellingDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGenerateNutritionalCounsellingDocumentMutation"
import { useChUpdateNutritionalCounsellingMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChUpdateNutritionalCounsellingMutation"
import {
  DOCUMENT_LANGUAGES_INCLUDE_ENGLISH,
  getDocumentLanguageLabel,
  useDefaultDocumentLanguage,
} from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ObesityTherapy } from "@/types/__generated__/union-types"

interface ChNutritionalCounsellingDrawerProps extends FetchKeyProps {
  progressNoteId: string
  id?: string
  nutritionalCounsellingRef?: chNutritionalCounsellingFragment$key
}

const AGE_FOR_OBESITY_THERAPY = 18
const DEFAULT_OBESITY_THERAPY: ObesityTherapy = "INDIVIDUAL_THERAPY"
const DEFAULT_TREATMENT_UNIT: string = "6"

const ChNutritionalCounsellingDrawer = withErrorBoundary(
  ({ progressNoteId, id, nutritionalCounsellingRef, fetchKey }: ChNutritionalCounsellingDrawerProps) => {
    const user = useUser()
    const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor"])
    const { t } = useTranslation()
    const { toast } = useToast()
    const patientId = usePatientParam()
    const [createNutritionalCounselling, isCreating] = useChCreateNutritionalCounsellingMutation()
    const [updateNutritionalCounselling, isUpdating] = useChUpdateNutritionalCounsellingMutation()
    const [deleteNutritionalCounselling] = useChDeleteNutritionalCounsellingMutation()
    const [generateNutritionalCounsellingDocument, isGenerating] = useChGenerateNutritionalCounsellingDocumentMutation()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)

    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const patientAge = usePatientFileContext((state) => state.patientFileInfo?.age?.years)
    const isAdult = !!(patientAge && patientAge >= AGE_FOR_OBESITY_THERAPY)
    const persistedData = useFragment(chNutritionalCounsellingFragment, nutritionalCounsellingRef ?? null)
    const updatedAt = persistedData?.updatedAt || persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy || persistedData?.createdBy
    const includeEnglishDocument = true
    const { documentLanguageIncludeEnglish } = useDefaultDocumentLanguage()
    const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId)
    const parsedData = isEditMode
      ? NutritionalCounsellingSchema.parse({
          ...persistedData,
          treatmentUnit: persistedData?.treatmentUnit.toString(),
          additionalInformation: persistedData?.additionalInformation ?? "",
          obesityTherapy: persistedData?.obesityTherapy ?? DEFAULT_OBESITY_THERAPY,
          healthcareProviderId: persistedData?.healthcareProviderId ?? "",
        })
      : null

    const form = useForm<NutritionalCounselling>({
      mode: "all",
      resolver: zodResolver(NutritionalCounsellingSchema),
      defaultValues:
        isEditMode && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: documentLanguageIncludeEnglish,
              reason: "SICKNESS",
              diagnosisCode: latestDiagnosisCode,
              treatmentUnit: DEFAULT_TREATMENT_UNIT,
              conditions: [],
              allergies: [],
              medicationIds: [],
              nutritionalConditions: [],
              obesityTherapy: DEFAULT_OBESITY_THERAPY,
            },
    })

    const {
      register,
      control,
      setValue,
      getValues,
      watch,
      resetField,
      formState: { errors, isDirty },
    } = form

    const nutritionalConditions = watch("nutritionalConditions")
    const isVisibleObesityTherapy = nutritionalConditions.includes("OBESITY") && !isAdult

    useDirtyDrawerMaker(isDirty)

    const onSubmit: SubmitHandler<NutritionalCounselling> = (data) => {
      if (!progressNoteId) return

      const formattedData = {
        ...data,
        isAdult,
        treatmentUnit: Number(data.treatmentUnit),
        additionalInformation:
          data.additionalInformation && data.additionalInformation.length > 0 ? data.additionalInformation : null,
        obesityTherapy: isVisibleObesityTherapy ? data.obesityTherapy : null,
        healthcareProviderId: data.healthcareProviderId || null,
      }

      if (isAddMode) {
        createNutritionalCounselling({
          variables: {
            input: {
              additionalInformation: formattedData.additionalInformation,
              allergies: formattedData.allergies,
              conditions: formattedData.conditions,
              healthcareProviderId: formattedData.healthcareProviderId,
              isAdult: formattedData.isAdult,
              language: formattedData.language,
              medicationIds: formattedData.medicationIds,
              nutritionalConditions: formattedData.nutritionalConditions,
              obesityTherapy: formattedData.obesityTherapy,
              reason: formattedData.reason,
              treatmentUnit: formattedData.treatmentUnit,
              patientId,
              progressNoteId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_createNutritionalPrescription.ch_NutritionalPrescription?.id
            if (id) {
              handleGenerateNutritionalCounselling(id, data)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const nutritionalPrescription = data?.ch_createNutritionalPrescription?.ch_NutritionalPrescription

              if (nutritionalPrescription?.id) {
                const newRecordProxy = store.get(nutritionalPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_nutritionalPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updateNutritionalCounselling({
          variables: {
            input: {
              additionalInformation: formattedData.additionalInformation,
              allergies: formattedData.allergies,
              conditions: formattedData.conditions,
              healthcareProviderId: formattedData.healthcareProviderId,
              isAdult: formattedData.isAdult,
              language: formattedData.language,
              medicationIds: formattedData.medicationIds,
              nutritionalConditions: formattedData.nutritionalConditions,
              obesityTherapy: formattedData.obesityTherapy,
              reason: formattedData.reason,
              treatmentUnit: formattedData.treatmentUnit,
              id,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_updateNutritionalPrescription.ch_NutritionalPrescription?.id
            if (id) {
              handleGenerateNutritionalCounselling(id, data)
            }
          },
        })
      }
    }

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_nutrition_close")
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deleteNutritionalCounselling({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.therapyPrescriptionNutritionalCounselling.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.therapyPrescriptionNutritionalCounselling.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const handleGenerateNutritionalCounselling = (id: string, data: NutritionalCounselling) => {
      const { diagnosisCode, medicationIds } = data
      generateNutritionalCounsellingDocument({
        id,
        diagnosisCode,
        medicationIds,
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="nutritional-counselling-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("episodeOfCare.progressNote.therapyPrescriptionNutritionalCounselling")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="nutritional-counselling-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({
                    field: { name, onChange, value, ref },
                    formState: { errors },
                    fieldState: { invalid },
                  }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} {...ref}>
                        <SelectTrigger hasError={invalid} className="w-56.5">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.therapyPrescription.documentLanguage")}
                              value={t(getDocumentLanguageLabel(value, includeEnglishDocument))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES_INCLUDE_ENGLISH.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="mb-8 flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.to")}</label>
                <div className="w-full">
                  <FormField
                    name="healthcareProviderId"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <CareProviderSearch
                        label={t("patient.therapyPrescription.healthcareProvider")}
                        searchType="THERAPY_PRESCRIPTION_NUTRITIONAL_COUNSELLING_DOCUMENT"
                        name={name}
                        value={value}
                        analyticsSectionName="documents_nutrition"
                        defaultAdvancedSearchFilters={{
                          groupType: "NUTRITIONIST",
                        }}
                        onSelect={onChange}
                        onRemove={() => onChange("")}
                        error={errors.healthcareProviderId?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.for")}</label>
                <div className="flex">
                  <div className="w-2/5">
                    <Input
                      {...register("treatmentUnit")}
                      data-testid="treatment-unit-input"
                      error={errors.treatmentUnit?.message}
                      translateError
                      label={t("patient.therapyPrescription.treatmentUnits")}
                      maxLength={2}
                      required
                      onChange={(e) => {
                        setValue("treatmentUnit", preventTextInput(e.target.value), {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 pt-8">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.therapyPrescription.reason")}
                </label>
                <div className="flex gap-4">
                  <FormField
                    control={control}
                    name="reason"
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        className="flex flex-wrap"
                        onValueChange={onChange}
                        defaultValue={value}
                        value={value}>
                        {REASONS.map((reason) => (
                          <RadioGroupItem
                            key={reason}
                            value={reason}
                            data-testid={`radio-item-${reason.toLowerCase()}`}>
                            <RadioGroupLabel>{t(`patient.therapyPrescription.reasons.${reason}`)}</RadioGroupLabel>
                          </RadioGroupItem>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="diagnosisCode"
                  render={({ field: { name, value, onChange }, fieldState: { invalid } }) => (
                    <Suspense fallback={null}>
                      <DiagnosisCode
                        defaultValue={isAddMode ? latestDiagnosisCode : persistedData?.diagnosisCode}
                        name={name}
                        errors={errors}
                        invalid={invalid}
                        value={value}
                        fetchKey={fetchKey}
                        onSelect={onChange}
                      />
                    </Suspense>
                  )}
                />
                <div>
                  <div
                    className={cn(
                      "mb-2",
                      errors.nutritionalConditions?.message &&
                        "outline-critical! rounded-md outline-2 outline-offset-4 outline-solid",
                    )}>
                    <label className="text-on-surface-subdued block pb-2 text-sm">
                      {t("patient.therapyPrescriptionNutritionalCounselling.condition") + "*"}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {NUTRITIONAL_CONDITIONS.map((condition) => (
                        <FormField
                          key={condition}
                          control={control}
                          name="nutritionalConditions"
                          render={({ field: { value = [], onChange } }) => (
                            <DetailCheckbox
                              key={condition}
                              checked={value.includes(condition)}
                              data-testid={`${condition.toLowerCase()}-condition-checkbox`}
                              onCheckedChange={(checked) => {
                                const checkedValues = checked
                                  ? [...value, condition].sort((a, b) => a.localeCompare(b))
                                  : value.filter((value) => value !== condition)

                                if (
                                  !checked &&
                                  condition === "OBESITY" &&
                                  getValues("obesityTherapy") !== persistedData?.obesityTherapy
                                ) {
                                  resetField("obesityTherapy")
                                }

                                onChange(checkedValues)
                              }}>
                              {t(
                                `patient.therapyPrescriptionNutritionalCounselling.nutritionalConditions.${condition}`,
                              )}
                            </DetailCheckbox>
                          )}
                        />
                      ))}
                    </div>
                  </div>
                  <TranslatedFieldError errors={errors} name="nutritionalConditions" />
                </div>

                {isVisibleObesityTherapy && (
                  <div>
                    <label className="text-on-surface-subdued block pb-2 text-sm">
                      {t("patient.therapyPrescriptionNutritionalCounselling.obesityTherapy") + "*"}
                    </label>
                    <FormField
                      control={control}
                      name="obesityTherapy"
                      render={({ field: { onChange, value } }) => (
                        <RadioGroup
                          className="flex flex-wrap"
                          onValueChange={onChange}
                          defaultValue={value}
                          value={value}>
                          {OBESITIES.map((obesity) => (
                            <RadioGroupItem
                              key={obesity}
                              value={obesity}
                              data-testid={`radio-item-${obesity.toLowerCase()}`}
                              className="h-auto min-h-8 text-left">
                              <RadioGroupLabel>
                                {t(`patient.therapyPrescriptionNutritionalCounselling.obesities.${obesity}`)}
                              </RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}

                <TextArea
                  {...register("additionalInformation")}
                  data-testid="additional-information-input"
                  error={errors.additionalInformation?.message}
                  translateError={true}
                  label={t("patient.therapyPrescription.additionalInfo")}
                  textLimit={textAreaDefaultMaxLength}
                />
              </div>
              <MedicalHistory />
            </DrawerBody>

            <DrawerFooter className="flex-wrap gap-2" data-testid="nutritional-counselling-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && (
                    <LastModificationInfo
                      updatedAt={updatedAt}
                      updatedBy={updatedBy}
                      data-testid="nutritional-counselling-last-modification-info"
                    />
                  )}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_nutrition_delete")
                      handleDelete()
                    }}
                    data-testid="nutritional-counselling-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="nutritional-counselling-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  isLoading={isCreating || isUpdating || isGenerating}
                  disabled={!hasSavePermission || (isEditMode && !isDirty)}
                  data-testid="nutritional-counselling-save-button"
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_nutrition_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChNutritionalCounsellingDrawer",
)

export { ChNutritionalCounsellingDrawer }
